import axios from 'axios';
import config from '@/config';
import * as constants from '../constants';
import { mapFilterToResponse } from '../utils/filters';
import { optionsMutationName } from '../utils/vehicleFilters';

const { endpoints } = config.level4;

const fetchCategories = ({ commit, dispatch }, data = {}) => new Promise((resolve, reject) => {
  commit(constants.LEVEL4_CATEGORIES_FETCH, []);
  axios({ url: endpoints.categories, data, method: 'POST' })
    .then((resp) => {
      commit(constants.LEVEL4_CATEGORIES_FETCH, resp.data);
      resolve(resp.data);
    })
    .catch((err) => {
      dispatch('core/showGeneralError', { err }, { root: true });
      reject(err);
    });
});

const fetchCreditLimit = ({ dispatch }) => new Promise((resolve, reject) => {
  axios({ url: endpoints.creditLimit })
    .then((resp) => {
      resolve(resp.data);
    })
    .catch((err) => {
      dispatch('core/showGeneralError', { err }, { root: true });
      reject(err);
    });
});

const fetchHotspots = ({ dispatch, commit }, id) => new Promise((resolve, reject) => {
  axios({ url: endpoints.hotspots(id) })
    .then((resp) => {
      commit(constants.LEVEL4_HOTSPOTS_FETCH, resp.data);
      resolve(resp.data);
    })
    .catch((err) => {
      dispatch('core/showGeneralError', { err }, { root: true });
      reject(err);
    });
});

const updateHotspots = ({ commit }, hotspots) => commit(constants.LEVEL4_HOTSPOTS_FETCH, hotspots);

const fetchConditions = ({ commit, dispatch, getters }, data = {}) => {
  const defaultFilters = {
    ...getters.filters,
    vehicleConditions: [],
    ...data,
  };

  return new Promise((resolve, reject) => {
    commit(constants.LEVEL4_CONDITIONS_FETCH, []);
    axios({ url: endpoints.conditions, data: defaultFilters, method: 'POST' })
      .then((resp) => {
        commit(constants.LEVEL4_CONDITIONS_FETCH, resp.data);
        resolve(resp.data);
      })
      .catch((err) => {
        dispatch('core/showGeneralError', { err }, { root: true });
        reject(err);
      });
  });
};

const fetchBoards = ({ commit, dispatch, getters }, data = {}) => {
  const defaultData = {
    ...getters.filters,
    ...data,
  };

  return new Promise((resolve, reject) => {
    commit(constants.LEVEL4_BOARDS_FETCH, []);
    axios({ url: endpoints.boards, data: defaultData, method: 'POST' })
      .then(({ data: respnseData }) => {
        commit(constants.LEVEL4_BOARDS_FETCH, respnseData);
        resolve(respnseData);
      })
      .catch((err) => {
        dispatch('core/showGeneralError', { err }, { root: true });
        reject(err);
      });
  });
};

const fetchVehicleFilter = ({ commit, dispatch, getters }, data = {}) => {
  const { filterType, filters = {} } = data;
  const defaultData = {
    ...getters.defaultVehicleFilters,
    ...getters.vehicleCurrentFilters,
    ...filters,
  };

  return new Promise((resolve, reject) => {
    commit(optionsMutationName(filterType), []);
    axios({ url: endpoints.vehicleFilter(filterType), data: defaultData, method: 'POST' })
      .then(({ data: responseData }) => {
        commit(optionsMutationName(filterType), responseData);
        resolve(responseData);
      })
      .catch((err) => {
        dispatch('core/showGeneralError', { err }, { root: true });
        reject(err);
      });
  });
};

const setVehicleCurrentFilters = ({ commit, getters }, data = {}) => new Promise((resolve) => {
  const newData = {
    ...getters.vehicleCurrentFilters,
    ...data,
  };

  commit(constants.LEVEL4_VEHICLE_CURRENT_FILTERS, newData);
  resolve(newData);
});

const resetVehicleFilters = ({ commit, getters, dispatch }) => new Promise((resolve) => {
  dispatch('setVehicleCurrentFilters', getters.defaultVehicleFilters);
  [
    ...constants.vehicleFiltersBasicTypes.slice(1),
    ...constants.vehicleFiltersVehicleTypes,
  ].forEach((item) => {
    commit(optionsMutationName(item.name), []);
  });
  resolve();
});

const fetchBoard = ({ commit, dispatch, getters }, data = {}) => {
  const defaultData = {
    ...getters.filters,
    vehicleConditions: mapFilterToResponse(getters.conditions),
    searchType: 'Simple',
    ...data,
  };

  return new Promise((resolve, reject) => {
    commit(constants.LEVEL4_BOARD_FETCH, []);
    axios({ url: endpoints.board, data: defaultData, method: 'POST' })
      .then(({ data: respnseData }) => {
        commit(constants.LEVEL4_BOARD_FETCH, respnseData);
        resolve(respnseData);
      })
      .catch((err) => {
        dispatch('core/showGeneralError', { err }, { root: true });
        reject(err);
      });
  });
};

const searchByVin = ({ commit, dispatch, getters }, vin) => {
  if (!vin) {
    commit(constants.LEVEL4_VEHICLE_DATA, {});
    localStorage.removeItem(constants.LEVEL4_VEHICLE_DATA);
    dispatch('fetchCategories', getters.defaultFilters);
    return {};
  }

  return new Promise((resolve, reject) => {
    dispatch('core/toggleLoader', true, { root: true });

    axios({ url: endpoints.vin(vin) })
      .then((resp) => {
        resolve(resp.data);
      })
      .catch((err) => {
        commit(constants.LEVEL4_VEHICLE_DATA, {});
        localStorage.removeItem(constants.LEVEL4_VEHICLE_DATA);
        dispatch('core/showGeneralError', { err }, { root: true });
        reject(err);
      })
      .finally(() => {
        dispatch('core/toggleLoader', false, { root: true });
      });
  });
};

const setVin = ({ commit, dispatch, getters }, vehicleData) => {
  commit(constants.LEVEL4_VEHICLE_DATA, vehicleData);
  localStorage.setItem(constants.LEVEL4_VEHICLE_DATA, JSON.stringify(vehicleData));
  dispatch('fetchCategories', getters.filters);
};

const searchByParts = ({ dispatch, getters }, data) => new Promise((resolve, reject) => {
  dispatch('core/toggleLoader', true, { root: true });

  axios({ url: endpoints.find, data, method: 'POST' })
    .then((resp) => {
      dispatch('fetchCategories', getters.filters);
      resolve(resp.data);
    })
    .catch((err) => {
      dispatch('core/showGeneralError', { err }, { root: true });
      reject(err);
    })
    .finally(() => {
      dispatch('core/toggleLoader', false, { root: true });
    });
});

const searchByFilter = ({ commit, dispatch, getters }) => {
  const data = getters.vehicleCurrentFilters;

  return new Promise((resolve, reject) => {
    dispatch('core/toggleLoader', true, { root: true });

    axios({ url: endpoints.vehicleSearch, data, method: 'POST' })
      .then((resp) => {
        commit(constants.LEVEL4_VEHICLE_DATA, resp.data);
        localStorage.setItem(constants.LEVEL4_VEHICLE_DATA, JSON.stringify(resp.data));
        dispatch('fetchCategories', getters.filters);
        resolve(resp.data);
      })
      .catch((err) => {
        commit(constants.LEVEL4_VEHICLE_DATA, {});
        localStorage.removeItem(constants.LEVEL4_VEHICLE_DATA);
        dispatch('core/showGeneralError', { err }, { root: true });
        reject(err);
      })
      .finally(() => {
        dispatch('core/toggleLoader', false, { root: true });
      });
  });
};

const fetchStock = ({ dispatch, getters }, sku) => {
  const { marke, produktart } = getters?.vehicleData?.VehicleInfo || {};
  const data = {
    sku,
    marke: marke || 'BMW',
    produktart: produktart || 'PKW',
  };

  return new Promise((resolve, reject) => {
    axios({ url: endpoints.stock, data, method: 'POST' })
      .then((resp) => {
        resolve(resp.data);
      })
      .catch((err) => {
        dispatch('core/showGeneralError', { err }, { root: true });
        reject(err);
      });
  });
};

export default {
  fetchCategories,
  fetchConditions,
  fetchBoards,
  fetchBoard,
  fetchHotspots,
  updateHotspots,
  searchByVin,
  setVin,
  searchByParts,
  fetchStock,
  fetchCreditLimit,
  fetchVehicleFilter,
  setVehicleCurrentFilters,
  searchByFilter,
  resetVehicleFilters,
};
